/**
 * Super Admin: MMM models (D5; spec §6, §3.4, §5.11; plan R5, R8, R20). Phase 1 trains Meridian outside the system, and
 * this page is the round trip, one workspace at a time:
 * - the config, read only, and the live 7 checks with box 1's labels;
 * - "ดาวน์โหลดข้อมูลสำหรับเทรน", enabled by the rule the route applies, beside the line that the file holds the
 *   client's outcome (revenue, or a count's units: T18a) and must be deleted after training. Both read the readiness
 *   row, whose metric the route writes;
 * - the upload, with the route's verdict verbatim;
 * - the version list, with "ใช้ version นี้" (making a version active stays the master's, O14).
 * Master only, as the rest of Super Admin: the row is hidden from anyone else, and the reads are the gate
 * (admin_list_workspaces, the master-only SELECT on mmm_model_versions, mmm_set_active_version). The download and the
 * upload are the routes', which /budget's box 1 shares (O13, O14).
 *
 * @module pages/admin/MmmModels
 */
import React, { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { YDMPageHero } from '@/components/brand';
import { ceilAt, floorAt, metricLabel, ReadinessChecks } from '@/components/budget/ReadinessCard';
import { VersionUploadForm } from '@/components/budget/VersionUploadForm';
import { OverviewBox, OverviewCard } from '@/components/overview/OverviewBox';
import { useBudgetData, type BudgetData } from '@/hooks/useBudgetData';
import { useKeyedRead } from '@/hooks/useKeyedRead';
import { listWorkspaces, type AdminWorkspace } from '@/services/adminService';
import { downloadDataset, listVersions, saveTextFile, setActiveVersion, type VersionListRow } from '@/services/budget/budgetAdminService';
import { OUTCOME_METRICS } from '@/services/budget/budgetContract';
import type { MmmConfig } from '@/services/budget/budgetReaders';
import { clientDataLine } from '@/services/budget/outcomeUnits';
import { checksOf, downloadAllowed, type ReadinessRow } from '@/services/budget/readinessRules';
import type { AdAccount } from '@/services/budget/scopeRows';
import { fmtShortDate, type TFn } from '@/services/channels/channelFormat';
import { dataDateOf } from '@/services/dataDate';
import { addDays } from '@/services/looker/lookerDateRange';
import { DASH, fmtPct } from '@/services/overview/overviewFormat';
import type { ReadState } from '@/services/overview/overviewTypes';
import { platformShortLabel } from '@/utils/platformLabel';

const BUTTON_CLASS = 'rounded-md bg-ink-950 px-3 py-1.5 text-sm font-medium text-white hover:bg-ink-900 disabled:opacity-50';
const ALERT_CLASS = 'mt-2 rounded-lg border border-error-700 px-3 py-2 text-sm text-error-700';
const CELL_CLASS = 'px-3 py-2 text-neutral-800';

/** A box whose reads are not all ready: failed once one failed (spec §7), otherwise loading. */
const Pending: React.FC<{ id: string; title: string; reads: ReadonlyArray<ReadState<unknown>>; onRetry: () => void }> = (
  { id, title, reads, onRetry },
) => (
  <OverviewBox id={id} title={title} state={{ status: reads.some((r) => r.status === 'failed') ? 'failed' : 'loading' }} onRetry={onRetry}>
    {() => null}
  </OverviewBox>
);

/** A route's or the RPC's refusal, verbatim, one line each. */
const Refusal: React.FC<{ title: string; message: string }> = ({ title, message }) => (
  <div role="alert" className={ALERT_CLASS}>
    <p className="font-semibold">{title}</p>
    <p className="whitespace-pre-wrap break-words">{message}</p>
  </div>
);

const accountText = (id: string, accounts: readonly AdAccount[]): string => {
  const a = accounts.find((x) => x.id === id);
  return a ? `${platformShortLabel(a.platform)} · ${a.accountName ?? a.externalAccountId}` : id;
};

const ConfigList: React.FC<{ config: MmmConfig; accounts: readonly AdAccount[] }> = ({ config, accounts }) => {
  const { t } = useTranslation();
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
      <dt className="text-neutral-700">{t('budgetAdmin.config.metric')}</dt>
      <dd className="text-ink-900">{metricLabel(config.outcomeMetric, t)}</dd>
      <dt className="text-neutral-700">{t('budgetAdmin.config.scope', { n: config.scopeAccountIds.length })}</dt>
      <dd>
        <ul className="text-ink-900">{config.scopeAccountIds.map((id) => <li key={id}>{accountText(id, accounts)}</li>)}</ul>
      </dd>
      <dt className="text-neutral-700">{t('budgetAdmin.config.confirmedAt')}</dt>
      <dd className="text-ink-900">{fmtShortDate(dataDateOf(config.confirmedAt), t)}</dd>
    </dl>
  );
};

/** The confirmed scope and metric, read only: box S and box 1 on /budget change them. */
const ConfigBox: React.FC<{ data: BudgetData }> = ({ data }) => {
  const { t } = useTranslation();
  const { config, accounts } = data;
  const title = t('budgetAdmin.config.title');
  if (config.status !== 'ready' || accounts.status !== 'ready') {
    return <Pending id="budget-admin-config" title={title} reads={[config, accounts]} onRetry={data.retry} />;
  }
  return (
    <OverviewCard id="budget-admin-config" title={title}>
      {config.data === null
        ? <p className="text-sm text-neutral-700">{t('budgetAdmin.config.none')}</p>
        : <ConfigList config={config.data} accounts={accounts.data} />}
    </OverviewCard>
  );
};

/** The live checks, as box 1 shows them: check 6 compares the config with the active version. */
const ChecksBox: React.FC<{ data: BudgetData }> = ({ data }) => {
  const { t } = useTranslation();
  const { readiness, config, active } = data;
  const title = t('budgetAdmin.checks');
  // get_mmm_readiness's null is a workspace the caller cannot see: the box's error, never seven ✕.
  if (readiness.status !== 'ready' || readiness.data === null || config.status !== 'ready' || active.status !== 'ready') {
    const seen: ReadState<unknown> = readiness.status === 'ready' && readiness.data === null ? { status: 'failed' } : readiness;
    return <Pending id="budget-admin-checks" title={title} reads={[seen, config, active]} onRetry={data.retry} />;
  }
  return (
    <OverviewCard id="budget-admin-checks" title={title}>
      <ReadinessChecks checks={checksOf(readiness.data, active.data)} metric={config.data?.outcomeMetric ?? 'revenue'} />
    </OverviewCard>
  );
};

interface DownloadProps {
  workspaceId: string;
  readiness: ReadState<ReadinessRow | null>;
}

const DownloadCard: React.FC<DownloadProps> = ({ workspaceId, readiness }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const r = readiness.status === 'ready' ? readiness.data : null;
  // The route's own rule, on no version (api/_lib/mmmDataset.ts): a version's scope never blocks a download (R19).
  const allowed = r !== null && downloadAllowed(r, checksOf(r, null));
  // The route writes the file from this row's metric, so whenever the button is enabled the line names it (T18a),
  // narrowed as box I does; with no row yet the button is disabled.
  const metric = OUTCOME_METRICS.find((m) => m === r?.outcomeMetric) ?? 'revenue';

  const download = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    const res = await downloadDataset(workspaceId);
    setBusy(false);
    if (res.success && res.data) saveTextFile(res.data.fileName, res.data.text);
    else setError(res.error ?? t('budgetAdmin.download.failed'));
  };

  return (
    <OverviewCard id="budget-admin-download" title={t('budgetAdmin.download.title')}>
      <button type="button" disabled={!allowed || busy} onClick={() => void download()} className={BUTTON_CLASS}>
        {t('budgetAdmin.download.button')}
      </button>
      <p className="mt-2 text-xs text-neutral-700">{clientDataLine(metric, t)}</p>
      {error !== null && <Refusal title={t('budgetAdmin.download.failed')} message={error} />}
    </OverviewCard>
  );
};

/** R² / MAPE / r-hat with box 1's directed rounding (T12h: R² and r-hat floored, MAPE ceiled); NULL reads "—". */
const qualityOf = (v: VersionListRow): string => [
  v.r2 === null ? DASH : floorAt(v.r2, 2).toFixed(2),
  fmtPct(v.mape === null ? null : ceilAt(v.mape, 3), 1),
  v.maxRhat === null ? DASH : floorAt(v.maxRhat, 2).toFixed(2),
].join(' / ');

/** A cell carries its own style, so a new column cannot move it onto another. */
interface Cell { text: string; className?: string; title?: string }

/** The trained day in Bangkok, and the data's days: the window's first Monday to its last Monday + 6 (spec §5.12). */
const cellsOf = (v: VersionListRow, t: TFn): Cell[] => [
  { text: `v${v.version}` },
  { text: fmtShortDate(dataDateOf(v.trainedAt), t) },
  { text: `${fmtShortDate(v.windowStart, t)} – ${fmtShortDate(addDays(v.windowEnd, 6), t)}` },
  { text: metricLabel(v.outcomeMetric, t) },
  { text: qualityOf(v), className: 'tabular-nums' },
  { text: v.uploadedByEmail ?? DASH },
  { text: v.note || DASH },
  // T17c (spec §3.4, §5.11): the pkl's name, and enough of its sha256 to match by eye, the whole of it on hover.
  { text: v.pklFileName, className: 'break-all' },
  { text: v.pklSha256.slice(0, 12), className: 'font-mono', title: v.pklSha256 },
];

/** One version: the active one says so, every other offers "ใช้ version นี้". */
const VersionRow: React.FC<{ v: VersionListRow; busy: boolean; onActivate: (id: string) => void }> = ({ v, busy, onActivate }) => {
  const { t } = useTranslation();
  return (
    <tr className="align-top">
      {cellsOf(v, t).map((c, i) => <td key={i} className={`${CELL_CLASS} ${c.className ?? ''}`} title={c.title}>{c.text}</td>)}
      <td className={CELL_CLASS}>
        {v.isActive
          ? <span className="font-medium text-success-700">{t('budgetAdmin.versions.active')}</span>
          : (
            <button type="button" disabled={busy} onClick={() => onActivate(v.id)}
              className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs font-medium text-ink-900 hover:bg-neutral-50 disabled:opacity-50">
              {t('budgetAdmin.versions.activate')}
            </button>
          )}
      </td>
    </tr>
  );
};

const VersionTable: React.FC<{ rows: readonly VersionListRow[]; onActivated: () => void }> = ({ rows, onActivated }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (rows.length === 0) return <p className="text-sm text-neutral-700">{t('budgetAdmin.versions.empty')}</p>;

  const activate = async (id: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await setActiveVersion(id);
      onActivated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const heads = [
    t('budgetAdmin.versions.col.version'), t('budgetAdmin.versions.col.trained'), t('budgetAdmin.versions.col.window'),
    t('budgetAdmin.versions.col.metric'), t('budgetAdmin.versions.col.quality'), t('budgetAdmin.versions.col.uploadedBy'),
    t('budgetAdmin.versions.col.note'), t('budgetAdmin.versions.col.pklFile'), t('budgetAdmin.versions.col.pklSha256'),
    t('budgetAdmin.versions.col.active'),
  ];
  return (
    <>
      {error !== null && <Refusal title={t('budgetAdmin.versions.activateFailed')} message={error} />}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50 text-xs text-neutral-700">
            <tr>{heads.map((h) => <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {rows.map((v) => <VersionRow key={v.id} v={v} busy={busy} onActivate={(id) => void activate(id)} />)}
          </tbody>
        </table>
      </div>
    </>
  );
};

const WorkspaceModels: React.FC<{ workspaceId: string }> = ({ workspaceId }) => {
  const { t } = useTranslation();
  const scope = useMemo(() => ({ workspaceIds: [workspaceId], workspaceId }), [workspaceId]);
  const data = useBudgetData(scope);
  const [listAttempt, setListAttempt] = useState(0);
  const versions = useKeyedRead(`budget-admin|versions|${workspaceId}|a:${listAttempt}`, () => listVersions(workspaceId), 'BudgetAdminVersions');
  const relist = (): void => setListAttempt((n) => n + 1);
  // A new active version moves check 6, so the checks are read again with the list.
  const reread = (): void => { relist(); data.retry(); };

  return (
    <>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ConfigBox data={data} />
        <ChecksBox data={data} />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <DownloadCard workspaceId={workspaceId} readiness={data.readiness} />
        <OverviewCard id="budget-admin-upload" title={t('budgetAdmin.upload.title')}>
          <VersionUploadForm workspaceId={workspaceId} onUploaded={reread} />
        </OverviewCard>
      </div>
      <OverviewBox id="budget-admin-versions" title={t('budgetAdmin.versions.title')}
        state={versions.status === 'ready' ? versions : { status: versions.status === 'failed' ? 'failed' : 'loading' }} onRetry={relist}>
        {(rows) => <VersionTable rows={rows} onActivated={reread} />}
      </OverviewBox>
    </>
  );
};

const readWorkspaces = async (): Promise<AdminWorkspace[]> => {
  const res = await listWorkspaces();
  if (!res.success || !res.data) throw new Error(res.error ?? 'workspaces read failed');
  return res.data;
};

const AdminMmmModels: React.FC = () => {
  const { t } = useTranslation();
  const pickerId = useId();
  const workspaces = useKeyedRead('budget-admin|workspaces', readWorkspaces, 'BudgetAdminWorkspaces');
  const [workspaceId, setWorkspaceId] = useState('');

  return (
    <div className="space-y-4 animate-fade-in">
      <YDMPageHero eyebrow="Super Admin" headline={t('budgetAdmin.title')} subtitle={t('budgetAdmin.subtitle')} />
      {workspaces.status === 'failed' && <p role="alert" className={ALERT_CLASS}>{t('budgetAdmin.workspacesFailed')}</p>}
      {workspaces.status === 'ready' && (
        <div className="flex items-center gap-2">
          <label htmlFor={pickerId} className="text-sm font-medium text-neutral-800">{t('budgetAdmin.workspace')}</label>
          <select id={pickerId} value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)}
            className="rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-sm text-ink-900">
            <option value="">{t('budgetAdmin.pickWorkspace')}</option>
            {workspaces.data.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
      )}
      {/* Keyed: a new workspace starts every read, the form and the messages afresh. */}
      {workspaceId !== '' && <WorkspaceModels key={workspaceId} workspaceId={workspaceId} />}
    </div>
  );
};

export default AdminMmmModels;
